/**
 * Transport for the three reports that publish their own proof: the periodic
 * tax return, the fixed-asset register, and the GRNI reconciliation.
 *
 * What they share is the reason they can be trusted. Each derives its figures
 * two or three independent ways and refuses to answer when the derivations
 * disagree — a 500 carrying the disagreeing numbers, never a 200 with a
 * caveat. A register that quietly disagrees with its control account is not a
 * report; it is a broken set of books that survives to year end.
 *
 * So the screens render the disagreement when it comes, rather than
 * "something went wrong": the numbers in that body are the whole diagnosis.
 */
import type { components } from "@erp/api-client";

import { apiFetch } from "@/lib/api/http";
import { ApiError } from "@/lib/api/errors";

type Schemas = components["schemas"];

export type TaxReturn = Schemas["TaxReturn"];
export type TaxTieOutEntry = Schemas["TaxTieOutEntry"];
export type FixedAssetRegister = Schemas["FixedAssetRegister"];
export type GRNIReconciliation = Schemas["GRNIReconciliation"];

/** The return is company-wide: a branch is a site of one legal entity with one
 *  VAT registration, so it is not a filing dimension. Periods, not date
 *  ranges, define it — an adjustment period can share calendar dates with an
 *  ordinary one. */
export function getTaxReturn(fiscalPeriodId: string, signal?: AbortSignal): Promise<TaxReturn> {
  return apiFetch<TaxReturn>("/reports/tax-return", {
    query: { period: fiscalPeriodId },
    signal,
  });
}

export function getFixedAssetRegister(
  fiscalPeriodId: string,
  signal?: AbortSignal,
): Promise<FixedAssetRegister> {
  return apiFetch<FixedAssetRegister>("/reports/fixed-asset-register", {
    query: { fiscal_period_id: fiscalPeriodId },
    signal,
  });
}

export function getGRNIReconciliation(
  currency: string,
  asOf: string | undefined,
  signal?: AbortSignal,
): Promise<GRNIReconciliation> {
  return apiFetch<GRNIReconciliation>("/reports/grni-reconciliation", {
    query: { currency, ...(asOf === undefined || asOf === "" ? {} : { as_of: asOf }) },
    signal,
  });
}

/**
 * The withheld-report body, whatever the report. Each carries its own numbers
 * under its own names; this hands back the extension object so the screen can
 * show them instead of discarding the diagnosis.
 */
export function withheldReport(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof ApiError) || error.status !== 500) return null;
  const problem = error.problem as Record<string, unknown> | undefined;
  if (problem === undefined) return null;
  const known = [
    "mismatches",
    "register_cost",
    "ledger_cost",
    "ledger_balance",
    "subledger_balance",
  ];
  return known.some((key) => key in problem) ? problem : null;
}
